// src/components/AIOptimization.tsx
//
// Holistic AI Optimization page.
// - Distinct from /resume/clarity (issues-only) and /resume/tailor-for-job (single-job tailoring)
// - Uses /auth/resume/optimize, which returns per-section rewrites + inline phrase-level suggestions
// - Two view modes selectable via toggle: Side-by-side OR Inline (Grammarly-style)
// - Persists accepted sections as a brand-new BuiltResume copy (original untouched)

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";

type InlineSuggestion = {
  phrase: string;
  rewrite: string;
  category: "wording" | "quantification" | "structural" | "alignment";
  reason: string;
};

type Section = {
  id: string;
  name: string;
  original: string;
  optimized: string;
  rationale: string;
  inlineSuggestions: InlineSuggestion[];
};

type MissingSection = { name: string; suggested: string };

type OptimizeResponse = {
  source: { id: string; title: string; type: "resume" | "built" };
  profileContextUsed: string;
  scope: string[];
  llmUsed: boolean;
  partial?: boolean;
  partialCount?: number;
  sections: Section[];
  missingSections: MissingSection[];
  overallScore: number;
  summary: string;
};

type UnavailableState = {
  message: string;
  retryAfterSeconds: number;
};

type ResumePick = { id: string; title: string; type: "resume" | "built" };

const CATEGORY_COLORS: Record<InlineSuggestion["category"], string> = {
  wording: "#1d4ed8",
  quantification: "#16a34a",
  structural: "#d97706",
  alignment: "#9333ea",
};

const CATEGORY_LABEL: Record<InlineSuggestion["category"], string> = {
  wording: "Wording",
  quantification: "Quantification",
  structural: "Structural",
  alignment: "Profile alignment",
};

const AIOptimization: React.FC = () => {
  const navigate = useNavigate();
  const [resumeOptions, setResumeOptions] = useState<ResumePick[]>([]);
  const [selectedResume, setSelectedResume] = useState<ResumePick | null>(null);
  const [view, setView] = useState<"side_by_side" | "inline">("side_by_side");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<OptimizeResponse | null>(null);
  // Set when the backend returns 503 — replaces fake heuristic output with an
  // honest "AI engine is unavailable, try again in N seconds" empty state.
  const [unavailable, setUnavailable] = useState<UnavailableState | null>(null);
  // Track which version of each section the user has accepted: 'original' | 'optimized'
  const [accepted, setAccepted] = useState<Record<string, "original" | "optimized">>({});
  const [acceptedMissing, setAcceptedMissing] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState(false);

  // Load picker options on mount
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axiosInstance.get("/auth/resumes").catch(() => ({ data: { resumes: [] } })),
      axiosInstance.get("/auth/resume-builder").catch(() => ({ data: { resumes: [] } })),
    ]).then(([uploaded, built]) => {
      if (cancelled) return;
      const options: ResumePick[] = [
        ...(uploaded.data?.resumes || []).map((r: any) => ({ id: r.id, title: r.fileName, type: "resume" as const })),
        ...(built.data?.resumes || []).map((r: any) => ({ id: r.id, title: r.title, type: "built" as const })),
      ];
      setResumeOptions(options);
      if (options.length > 0) setSelectedResume(options[0]);
    });
    return () => { cancelled = true; };
  }, []);

  const runOptimize = async () => {
    if (!selectedResume) {
      toast.error("Pick a resume to optimize");
      return;
    }
    setLoading(true);
    setResult(null);
    setUnavailable(null);
    setAccepted({});
    setAcceptedMissing({});

    try {
      const body =
        selectedResume.type === "built"
          ? { builtResumeId: selectedResume.id }
          : { resumeId: selectedResume.id };
      const r = await axiosInstance.post<OptimizeResponse>("/auth/resume/optimize", body);
      setResult(r.data);
      // Default each section to "optimized" so accepting all is the common path
      const defaults: Record<string, "original" | "optimized"> = {};
      for (const s of r.data.sections) defaults[s.id] = "optimized";
      setAccepted(defaults);
      if (r.data.partial) {
        toast(
          `${r.data.partialCount ?? 1} section${(r.data.partialCount ?? 1) === 1 ? "" : "s"} couldn't be optimized — kept as-is. Re-optimize to retry.`,
          { icon: "⚠️", duration: 6000 }
        );
      }
    } catch (e: any) {
      // Backend now returns 503 with code:LLM_UNAVAILABLE when the AI engine
      // can't actually personalize the resume. Show an honest empty state
      // instead of fake heuristic sections.
      if (e.response?.status === 503 && e.response?.data?.code === "LLM_UNAVAILABLE") {
        setUnavailable({
          message: e.response.data.error || "AI engine is temporarily unavailable.",
          retryAfterSeconds: e.response.data.retryAfterSeconds || 60,
        });
      } else {
        toast.error(e.response?.data?.error || "Optimization failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!result) return;
    const sections = result.sections.map((s) => ({
      id: s.id,
      name: s.name,
      content: accepted[s.id] === "optimized" ? s.optimized : s.original,
    }));
    const missingSections = result.missingSections.filter((_, i) => acceptedMissing[i]);

    setSaving(true);
    try {
      const r = await axiosInstance.post("/auth/resume/optimize/save", {
        sourceTitle: result.source.title,
        sections,
        missingSections,
      });
      toast.success(`Saved as: ${r.data.builtResume.title}`);
      navigate("/resumes");
    } catch (e: any) {
      toast.error(e.response?.data?.error || "Could not save optimized resume");
    } finally {
      setSaving(false);
    }
  };

  const acceptedCount = useMemo(() => {
    return Object.values(accepted).filter((v) => v === "optimized").length;
  }, [accepted]);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: 24 }}>
      <button
        onClick={() => navigate("/dashboard")}
        style={{
          background: "transparent",
          border: "1px solid #cbd5e1",
          color: "#475569",
          borderRadius: 6,
          padding: "6px 14px",
          fontWeight: 600,
          cursor: "pointer",
          fontSize: 13,
          marginBottom: 20,
        }}
      >
        ← Back to Dashboard
      </button>

      <h1 style={{ margin: "0 0 8px", fontSize: 28, fontWeight: 800, color: "#0f172a" }}>
        ✨ AI Optimization
      </h1>
      <p style={{ margin: "0 0 20px", color: "#475569", lineHeight: 1.6 }}>
        AI rewrites your resume to be stronger, more concrete, and aligned with your career direction.
        Distinct from <strong>Clarity</strong> (issue spotter) and <strong>Tailor for a job</strong> (single-posting rewrite) —
        this one optimizes holistically for your profile.
      </p>

      {/* Picker + run row */}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-end",
          padding: 16,
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          marginBottom: 20,
        }}
      >
        <div style={{ flex: 1, minWidth: 260 }}>
          <label htmlFor="resume-pick" style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
            Resume to optimize
          </label>
          <select
            id="resume-pick"
            value={selectedResume ? `${selectedResume.type}:${selectedResume.id}` : ""}
            onChange={(e) => {
              const [type, id] = e.target.value.split(":");
              const opt = resumeOptions.find((o) => o.type === type && o.id === id);
              setSelectedResume(opt || null);
            }}
            style={{ width: "100%", padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14, background: "#fff" }}
          >
            {resumeOptions.length === 0 ? (
              <option value="">No resumes found — upload or build one first</option>
            ) : (
              resumeOptions.map((opt) => (
                <option key={`${opt.type}:${opt.id}`} value={`${opt.type}:${opt.id}`}>
                  {opt.type === "built" ? "📝 " : "📄 "} {opt.title}
                </option>
              ))
            )}
          </select>
        </div>
        <button
          onClick={runOptimize}
          disabled={loading || !selectedResume}
          style={{
            background: loading ? "#94a3b8" : "#1d4ed8",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "12px 24px",
            fontWeight: 700,
            cursor: loading ? "wait" : "pointer",
            fontSize: 14,
          }}
        >
          {loading ? "Optimizing..." : result ? "Re-optimize" : "✨ Optimize"}
        </button>
      </div>

      {/* Results */}
      {result && (
        <>
          {/* Partial-completion banner — appears when some sections succeeded
              but others failed. Lets the user retry without losing the good ones. */}
          {result.partial && (
            <div
              role="alert"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: 12,
                background: "#fef3c7",
                border: "1px solid #fcd34d",
                borderRadius: 8,
                marginBottom: 12,
                color: "#78350f",
              }}
            >
              <span style={{ fontSize: 13 }}>
                ⚠️ <strong>Partial result:</strong> {result.partialCount ?? 1} section{(result.partialCount ?? 1) === 1 ? " was" : "s were"} kept as-is because the AI engine timed out on those. The rest were optimized normally.
              </span>
              <button
                onClick={runOptimize}
                style={{
                  background: "#d97706",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 14px",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontSize: 12,
                  flexShrink: 0,
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* Header strip — view toggle + score + save */}
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              padding: 14,
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Overall score
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: result.overallScore >= 70 ? "#16a34a" : result.overallScore >= 50 ? "#d97706" : "#dc2626", lineHeight: 1 }}>
                  {result.overallScore}/100
                </div>
              </div>
              <div style={{ borderLeft: "1px solid #e2e8f0", paddingLeft: 16, fontSize: 13, color: "#475569", maxWidth: 480 }}>
                {result.summary}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div role="group" aria-label="View mode" style={{ display: "flex", border: "1px solid #cbd5e1", borderRadius: 6, overflow: "hidden" }}>
                <button
                  onClick={() => setView("side_by_side")}
                  aria-pressed={view === "side_by_side"}
                  style={{
                    background: view === "side_by_side" ? "#1d4ed8" : "#fff",
                    color: view === "side_by_side" ? "#fff" : "#475569",
                    border: "none",
                    padding: "8px 14px",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  ⇆ Side-by-side
                </button>
                <button
                  onClick={() => setView("inline")}
                  aria-pressed={view === "inline"}
                  style={{
                    background: view === "inline" ? "#1d4ed8" : "#fff",
                    color: view === "inline" ? "#fff" : "#475569",
                    border: "none",
                    padding: "8px 14px",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  ☰ Inline
                </button>
              </div>
              <button
                onClick={handleSave}
                disabled={saving || acceptedCount === 0}
                style={{
                  background: "#16a34a",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "10px 18px",
                  fontWeight: 700,
                  cursor: saving ? "wait" : "pointer",
                  fontSize: 14,
                }}
              >
                {saving ? "Saving..." : `Save (${acceptedCount} section${acceptedCount === 1 ? "" : "s"})`}
              </button>
            </div>
          </div>

          {/* Sections */}
          {view === "side_by_side"
            ? <SideBySideView result={result} accepted={accepted} setAccepted={setAccepted} />
            : <InlineView result={result} />
          }

          {/* Missing sections */}
          {result.missingSections.length > 0 && (
            <section style={{ marginTop: 24, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: 16 }}>
              <h2 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: "#92400e" }}>
                💡 Sections worth adding
              </h2>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {result.missingSections.map((ms, idx) => (
                  <li
                    key={idx}
                    style={{
                      background: "#fff",
                      border: "1px solid #fde68a",
                      borderRadius: 8,
                      padding: 12,
                      display: "flex",
                      gap: 12,
                    }}
                  >
                    <input
                      id={`missing-${idx}`}
                      type="checkbox"
                      checked={!!acceptedMissing[idx]}
                      onChange={(e) => setAcceptedMissing((m) => ({ ...m, [idx]: e.target.checked }))}
                      style={{ marginTop: 3 }}
                    />
                    <div style={{ flex: 1 }}>
                      <label htmlFor={`missing-${idx}`} style={{ fontWeight: 700, color: "#92400e", cursor: "pointer" }}>
                        {ms.name}
                      </label>
                      <pre style={{ margin: "4px 0 0", whiteSpace: "pre-wrap", color: "#713f12", fontSize: 13, fontFamily: "inherit" }}>
                        {ms.suggested}
                      </pre>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Profile context disclosure */}
          <details style={{ marginTop: 16, background: "#f1f5f9", padding: 10, borderRadius: 6, fontSize: 12, color: "#475569" }}>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>Profile context used by the optimizer</summary>
            <pre style={{ whiteSpace: "pre-wrap", marginTop: 6, fontFamily: "inherit" }}>{result.profileContextUsed}</pre>
          </details>
        </>
      )}

      {/* Service-unavailable empty state — replaces the old fake heuristic output */}
      {unavailable && !loading && (
        <div
          role="alert"
          style={{
            padding: 32,
            textAlign: "center",
            background: "#fef3c7",
            border: "1px solid #fcd34d",
            borderRadius: 8,
          }}
        >
          <div style={{ fontSize: 36, marginBottom: 8 }} aria-hidden="true">⚠️</div>
          <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: "#78350f" }}>
            AI engine is temporarily unavailable
          </h2>
          <p style={{ margin: "0 0 12px", color: "#92400e", maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}>
            {unavailable.message} We don't show generic suggestions here — when AI optimization can't actually personalize your resume to your profile, we tell you directly so you can retry.
          </p>
          <p style={{ margin: "0 0 16px", color: "#92400e", fontSize: 13 }}>
            Suggested wait: <strong>{unavailable.retryAfterSeconds}&nbsp;seconds</strong> before retrying.
          </p>
          <button
            onClick={runOptimize}
            disabled={loading}
            style={{
              background: "#d97706",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "10px 22px",
              fontWeight: 700,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            🔄 Retry now
          </button>
        </div>
      )}

      {!result && !loading && !unavailable && (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: 8 }}>
          Pick a resume and hit <strong>Optimize</strong> to see AI rewrites side-by-side or inline.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Side-by-side view
// ---------------------------------------------------------------------------
const SideBySideView: React.FC<{
  result: OptimizeResponse;
  accepted: Record<string, "original" | "optimized">;
  setAccepted: React.Dispatch<React.SetStateAction<Record<string, "original" | "optimized">>>;
}> = ({ result, accepted, setAccepted }) => {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 16 }}>
      {result.sections.map((s) => {
        const isUnchanged = s.original.trim() === s.optimized.trim();
        const choice = accepted[s.id] || "optimized";
        return (
          <li
            key={s.id}
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 16,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{s.name}</h3>
                <p style={{ margin: 0, fontSize: 13, color: "#475569", fontStyle: "italic" }}>{s.rationale}</p>
              </div>
              {isUnchanged ? (
                <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                  Already strong
                </span>
              ) : (
                <div role="group" aria-label={`Choice for ${s.name}`} style={{ display: "flex", border: "1px solid #cbd5e1", borderRadius: 6, overflow: "hidden", flexShrink: 0 }}>
                  <button
                    onClick={() => setAccepted((a) => ({ ...a, [s.id]: "original" }))}
                    aria-pressed={choice === "original"}
                    style={{
                      background: choice === "original" ? "#475569" : "#fff",
                      color: choice === "original" ? "#fff" : "#475569",
                      border: "none",
                      padding: "6px 12px",
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    Keep original
                  </button>
                  <button
                    onClick={() => setAccepted((a) => ({ ...a, [s.id]: "optimized" }))}
                    aria-pressed={choice === "optimized"}
                    style={{
                      background: choice === "optimized" ? "#16a34a" : "#fff",
                      color: choice === "optimized" ? "#fff" : "#475569",
                      border: "none",
                      padding: "6px 12px",
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    ✓ Apply
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                  Original
                </div>
                <pre style={{
                  margin: 0, padding: 12, background: choice === "original" ? "#f1f5f9" : "#fafafa",
                  border: choice === "original" ? "2px solid #475569" : "1px solid #e2e8f0",
                  borderRadius: 6, whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13, color: "#334155",
                  minHeight: 60,
                }}>
                  {s.original}
                </pre>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                  AI optimized
                </div>
                <pre style={{
                  margin: 0, padding: 12, background: choice === "optimized" ? "#f0fdf4" : "#fafafa",
                  border: choice === "optimized" ? "2px solid #16a34a" : "1px solid #e2e8f0",
                  borderRadius: 6, whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13, color: "#0f172a",
                  minHeight: 60,
                }}>
                  {s.optimized}
                </pre>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

// ---------------------------------------------------------------------------
// Inline (Grammarly-style) view
// ---------------------------------------------------------------------------
const InlineView: React.FC<{ result: OptimizeResponse }> = ({ result }) => {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 16 }}>
      {result.sections.map((s) => (
        <InlineSection key={s.id} section={s} />
      ))}
    </ul>
  );
};

const InlineSection: React.FC<{ section: Section }> = ({ section }) => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  // Render section text with highlighted phrases. Walks the original text,
  // splitting at suggestion phrase boundaries (case-sensitive substring match).
  const segments = useMemo(() => buildSegments(section.original, section.inlineSuggestions), [section]);

  return (
    <li
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: 16,
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{section.name}</h3>
        <span style={{ fontSize: 12, color: "#64748b" }}>
          {section.inlineSuggestions.length} suggestion{section.inlineSuggestions.length === 1 ? "" : "s"}
        </span>
      </div>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#475569", fontStyle: "italic" }}>{section.rationale}</p>

      <div style={{ whiteSpace: "pre-wrap", fontSize: 14, color: "#334155", lineHeight: 1.7, fontFamily: "inherit" }}>
        {segments.map((seg, idx) =>
          seg.suggestion === null ? (
            <span key={idx}>{seg.text}</span>
          ) : (
            <button
              key={idx}
              onClick={() => setOpenIdx(openIdx === seg.suggestionIdx ? null : seg.suggestionIdx)}
              aria-expanded={openIdx === seg.suggestionIdx}
              aria-label={`Suggestion: ${seg.suggestion.reason}`}
              style={{
                background: `${CATEGORY_COLORS[seg.suggestion.category]}22`,
                color: CATEGORY_COLORS[seg.suggestion.category],
                border: `1px dotted ${CATEGORY_COLORS[seg.suggestion.category]}`,
                borderRadius: 3,
                padding: "0 2px",
                cursor: "pointer",
                font: "inherit",
                position: "relative",
              }}
            >
              {seg.text}
            </button>
          )
        )}
      </div>

      {openIdx !== null && section.inlineSuggestions[openIdx] && (
        <div
          role="region"
          aria-label="Suggestion details"
          style={{
            marginTop: 12,
            padding: 12,
            background: `${CATEGORY_COLORS[section.inlineSuggestions[openIdx].category]}11`,
            border: `1px solid ${CATEGORY_COLORS[section.inlineSuggestions[openIdx].category]}55`,
            borderRadius: 8,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <span
              style={{
                background: CATEGORY_COLORS[section.inlineSuggestions[openIdx].category],
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: 999,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {CATEGORY_LABEL[section.inlineSuggestions[openIdx].category]}
            </span>
          </div>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            <span style={{ color: "#94a3b8" }}>Suggested: </span>
            <strong style={{ color: "#0f172a" }}>{section.inlineSuggestions[openIdx].rewrite}</strong>
          </div>
          <div style={{ fontSize: 12, color: "#475569" }}>
            {section.inlineSuggestions[openIdx].reason}
          </div>
        </div>
      )}
    </li>
  );
};

type Segment =
  | { text: string; suggestion: null }
  | { text: string; suggestion: InlineSuggestion; suggestionIdx: number };

function buildSegments(text: string, suggestions: InlineSuggestion[]): Segment[] {
  if (!suggestions.length) return [{ text, suggestion: null }];

  // Find each phrase position; if duplicate phrases, only highlight the first match.
  const marks: { start: number; end: number; idx: number }[] = [];
  for (let i = 0; i < suggestions.length; i++) {
    const phrase = suggestions[i].phrase;
    if (!phrase) continue;
    const start = text.indexOf(phrase);
    if (start === -1) continue;
    // skip if it would overlap an existing mark
    const overlap = marks.some((m) => start < m.end && start + phrase.length > m.start);
    if (overlap) continue;
    marks.push({ start, end: start + phrase.length, idx: i });
  }
  marks.sort((a, b) => a.start - b.start);

  const out: Segment[] = [];
  let cursor = 0;
  for (const m of marks) {
    if (m.start > cursor) out.push({ text: text.slice(cursor, m.start), suggestion: null });
    out.push({ text: text.slice(m.start, m.end), suggestion: suggestions[m.idx], suggestionIdx: m.idx });
    cursor = m.end;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), suggestion: null });
  return out.length ? out : [{ text, suggestion: null }];
}

export default AIOptimization;
